import * as React from "react";
import {useState} from "react";
import {Product} from "../../../model/product/Product.model";
import {ProductRatingType} from "../../../model/product/ProductRatingType";
import {RatingStatus} from "../rating/RatingStatus.component";
import InReportsRequester from "../in-reports/InReports.component";
import ComponentRatingDetail from "../rating/ComponentRatingDetail";
import {FormattedMessage} from "react-intl";
import {UserTypeEnum} from "../../../model/UserInfo.model";
import MarketIdentifiersGenerator from "../MarketIdentifiers.component";
import {trimToNearestWord} from "../../../utils/commonUtil";
import EsgRatingDetail from "../rating/EsgRatingDetail";
import {ProductMeetingNote} from "../meeting-notes/ProductMeetingNote";

export interface IInvestmentDueDiligence {
    product: Product;
    oddIqEnabled: boolean;
    userType: UserTypeEnum;
    mpCollapsedLength: number;
    shouldRenderESG: boolean;
}

export const InvestmentDueDiligence: React.FunctionComponent<IInvestmentDueDiligence> = (props) => {
    const [mpExpanded, setMpExpanded] = useState(false);
    const [mdExpanded, setMdExpanded] = useState(false);

    const isFundamentallyRated = props.product.currentRating.ratingType === ProductRatingType.FUNDAMENTAL;

    const renderRatingStatus = (product: Product) => {
        return <RatingStatus {...{
            currentRating: product.currentRating.value,
            currentRatingDate: product.currentRating.date,
            previousRating: product.previousRating && product.previousRating.value,
            previousRatingDate: product.previousRating && product.previousRating.date,
            ratingType: product.currentRating.ratingType,
            isOperationalDueDiligenceRating: false,
            hasOddDetails: false,
            productBackstopId: product.backstopId,
            showTooltip: true,
            oddIqEnabled: props.oddIqEnabled,
        }} />;
    };

    const renderProductDetail = () => {
        return <div className="product__preview-container">
                <div>
                    {renderRatingStatus(props.product)}
                </div>
                <div className="product__preview">
                    <div className="rating-comments__container">
                        <div className="rating-comments__paragraph" data-testid="rating-comments__paragraph">
                            {isFundamentallyRated ? props.product.overallRatingComments : ""}
                        </div>
                    </div>
                    <div className="product__idd-documents-container" data-testid="product__documents-container">
                        <InReportsRequester productBackstopId={props.product.backstopId}/>
                        <ProductMeetingNote productBackstopId={props.product.backstopId}/>
                    </div>
                </div>
            </div>;
    };

    const renderMoreLess = (
        mp: string, expanded: Boolean, setExpandedFunction: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        return mp.length > props.mpCollapsedLength
            ? <span className="product__more-less-toggle clickable link-16"
                    onClick={() => setExpandedFunction(!expanded)}
                    data-testid="product__more-less-toggle">
                    {expanded ? "Less" : "More"}
                <div className={`fal ${expanded ? "fa-angle-up" : "fa-angle-down"} product__more-less-arrow`}
                     data-testid="product__more-less-arrow"/>
                </span>
            : null;
    };

    const getTranslatedString = (mp: string) => {
        if (mp === "There are no major developments at this time") {
            return <FormattedMessage id={"product.there-are-no-major-development"} defaultMessage={mp} />;
        }

        if (mp === "There are no monitoring points at this time") {
            return <FormattedMessage id={"product.there-are-no-monitoring-points"} defaultMessage={mp} />;
        }

        return mp;
    };

    const getMonitoringPointsText = (mp: string, expanded: boolean) => {
        return (mp.length > props.mpCollapsedLength && !expanded)
            ? trimToNearestWord(mp, props.mpCollapsedLength) + "..."
            : getTranslatedString(mp);
    };

    const renderMonitoringPoints = () => {
        return props.product.currentRating.ratingType === ProductRatingType.FUNDAMENTAL
            ? <div className="product__monitoring-points-container" data-testid="product__monitoring-points-container">
                <h2><FormattedMessage id={"product.monitoring-point"} defaultMessage="Monitoring Points" /></h2>
                <div className="product__monitoring-points" data-testid="product__monitoring-points">
                    {getMonitoringPointsText(props.product.monitoringPoints!, mpExpanded)}
                    {renderMoreLess(props.product.monitoringPoints!, mpExpanded, setMpExpanded)}
                </div>
            </div>
            : null;
    };

    const renderMajorDevelopments = () => {
        return props.product.currentRating.ratingType === ProductRatingType.FUNDAMENTAL
            ? <div className="product__major-developments-container" data-testid="product__major-developments-container">
                <h2><FormattedMessage id="product.major-development" defaultMessage="Major Developments" /></h2>
                <div className="product__major-developments" data-testid="product__major-developments">
                    {getMonitoringPointsText(props.product.majorDevelopments!, mdExpanded)}
                    {renderMoreLess(props.product.majorDevelopments!, mdExpanded, setMdExpanded)}
                </div>
            </div>
            : null;
    };

    const renderDisclaimer = (product: Product) => {
        return product.currentRating.ratingType === ProductRatingType.FUNDAMENTAL
        ? <div/>
        : <div className="product__disclaimer body-12 product__content-align">
            <FormattedMessage
                id="product.disclaimer"
                defaultMessage="* Our Aon InForm Assessment is a quantitative tool designed to issue Qualified and Sell ratings only. Prior to issuing a Buy recommendation, managers must be fully vetted on a qualitative basis."
            />
        </div>;
    };

    const renderEsgSubcomponent = () => {
        return props.product.esgRating && props.shouldRenderESG
            ? <EsgRatingDetail
                title={"Environmental, Social, and Governance"}
                componentRating={props.product.esgRating}
                ratingType={props.product.currentRating.ratingType}
            />
            : null;
    };

    const renderComponents = () => {
        const product = props.product;
        return <div className="product__components">
            <ComponentRatingDetail title={"Business"} componentRating={props.product.businessRating} ratingType={props.product.currentRating.ratingType}/>
            <ComponentRatingDetail title={"Investment Staff"} componentRating={product.staffRating} ratingType={product.currentRating.ratingType}/>
            <ComponentRatingDetail title={"Investment Process"} componentRating={product.processRating} ratingType={product.currentRating.ratingType}/>
            <ComponentRatingDetail title={"Risk Management"} componentRating={product.riskRating} ratingType={product.currentRating.ratingType}/>
            <ComponentRatingDetail title={"Performance"} componentRating={product.performanceAnalysisRating} ratingType={product.currentRating.ratingType}/>
            <ComponentRatingDetail title={"Terms & Conditions"} componentRating={product.tncRating} ratingType={product.currentRating.ratingType}/>
            {renderEsgSubcomponent()}
            {renderDisclaimer(product)}
        </div>;
    };

    const renderMarketIdentifiers = () => {
        if (props.userType === UserTypeEnum.ADMIN) {
            const GeneratedComponent = MarketIdentifiersGenerator(props.product.backstopId);
            return  <GeneratedComponent className="product__market-identifiers"/>;
        }
    };

    return <div className="product__idd" data-testid="product__idd">
        {renderProductDetail()}
        {renderMajorDevelopments()}
        {renderMonitoringPoints()}
        {renderComponents()}
        {renderMarketIdentifiers()}
    </div>;
};

